import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useGame } from '../context/GameContext';
import { partyCreate, partyGet, partyJoin, partySetSeed, partyReset, partyDraftStart, partyDraftPick } from '../game/party';
import { CLASSES } from '../game/classes';
import { CARD_POOL_COMMON, CARD_POOL_UNCOMMON, CARD_POOL_RARE } from '../game/cards';
import GameCard from '../components/GameCard';
import { genSeed, makeRng, shuffle } from '../game/rng';
import { ArrowLeft, Users, Play, Copy, Check, Crown, Radio, RotateCw, Shuffle } from 'lucide-react';

const DRAFT_POOL_SIZE = 20;
const DRAFT_PICKS = 10;

export default function Party() {
  const { playerName, goToMenu, joinPartyAndStartRun, hostSeedAndStart, partyState, setPartyState, setDraftDeck, beginClassSelectWithDraft, selectedClass } = useGame();
  const [mode, setMode] = useState('menu'); // menu | lobby
  const [codeInput, setCodeInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [seedInput, setSeedInput] = useState(genSeed());
  const [copied, setCopied] = useState(false);
  const pollRef = useRef(null);

  const myTag = (playerName || '').toUpperCase();

  const startPolling = (code) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      if (document.visibilityState !== 'visible') return;
      const p = await partyGet(code);
      if (p) setPartyState(p);
    }, 2500);
  };
  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };
  useEffect(() => () => stopPolling(), []);

  const doHost = async () => {
    setBusy(true);
    const p = await partyCreate(playerName || 'ANON');
    setBusy(false);
    if (p) { setPartyState(p); setMode('lobby'); startPolling(p.room_code); }
  };
  const doJoin = async () => {
    const code = codeInput.trim().toUpperCase();
    if (!code) return;
    setBusy(true);
    const p = await partyJoin(code, playerName || 'ANON');
    setBusy(false);
    if (p) { setPartyState(p); setMode('lobby'); startPolling(p.room_code); }
  };

  const hostStart = async () => {
    if (!partyState) return;
    const p = await partySetSeed(partyState.room_code, myTag, seedInput.toUpperCase(), 0);
    if (p) { setPartyState(p); stopPolling(); hostSeedAndStart(p); }
  };
  const joinerStart = () => {
    if (!partyState?.seed) return;
    stopPolling();
    joinPartyAndStartRun(partyState);
  };
  const doReset = async () => {
    if (!partyState) return;
    const p = await partyReset(partyState.room_code, myTag);
    if (p) { setPartyState(p); setSeedInput(genSeed()); }
  };

  const startDraft = async () => {
    if (!partyState) return;
    const seed = seedInput.toUpperCase();
    const rng = makeRng(seed + '-partydraft');
    const combined = [
      ...CARD_POOL_COMMON, ...CARD_POOL_COMMON,
      ...CARD_POOL_UNCOMMON, ...CARD_POOL_RARE,
    ];
    const pool = shuffle(rng, combined).slice(0, DRAFT_POOL_SIZE);
    const p = await partyDraftStart(partyState.room_code, myTag, pool, seed, DRAFT_PICKS);
    if (p) setPartyState(p);
  };

  const pickCard = async (idx) => {
    if (!partyState?.draft_state) return;
    const p = await partyDraftPick(partyState.room_code, myTag, idx);
    if (p) setPartyState(p);
  };

  const finishDraftAndPlay = () => {
    if (!partyState?.draft_state?.done) return;
    const myPicks = partyState.draft_state.picks_by_tag?.[myTag] || [];
    const filler = ['yap','yap','cope','cope','cope'];
    setDraftDeck([...filler, ...myPicks]);
    stopPolling();
    // Use draft flow so ClassSelect knows to inject the drafted deck
    beginClassSelectWithDraft(selectedClass || 'sigma', partyState.seed || partyState.draft_state.seed);
  };

  const copyCode = () => {
    if (!partyState) return;
    navigator.clipboard?.writeText(partyState.room_code).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    });
  };

  // Menu view
  if (mode === 'menu') {
    return (
      <div className="min-h-screen w-full px-6 py-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between">
          <button className="brutal-btn text-sm" onClick={goToMenu} data-testid="party-back-button">
            <ArrowLeft size={16} /> BACK
          </button>
          <div className="w-16" />
        </div>
        <h1 className="mt-4 font-display text-5xl sm:text-7xl font-black uppercase text-[color:var(--neon)] text-center">
          PARTY <span className="text-[color:var(--pink)]">SLOP</span>
        </h1>
        <div className="mt-2 text-center text-sm uppercase tracking-widest text-[color:var(--text-dim)]">
          up to 4 friends · same seed · live score updates · async draft support
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="brutal-border p-6 bg-[#0a0a0a] brutal-shadow">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[color:var(--text-dim)] mb-2">
              <Crown size={14}/> HOST A PARTY
            </div>
            <div className="text-sm text-[color:var(--text-dim)] mt-2">Create a room, share the 4-char code.</div>
            <button className="brutal-btn w-full mt-4" onClick={doHost} disabled={busy} data-testid="party-host-button">
              <Users size={16}/> {busy ? 'CREATING...' : 'CREATE ROOM'}
            </button>
          </div>
          <div className="brutal-border-pink p-6 bg-[#0a0a0a] brutal-shadow-pink">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[color:var(--text-dim)] mb-2">
              <Radio size={14}/> JOIN A PARTY
            </div>
            <input
              value={codeInput}
              onChange={e => setCodeInput(e.target.value.toUpperCase())}
              placeholder="4-CHAR CODE"
              maxLength={5}
              className="w-full bg-black border-2 border-[color:var(--muted)] focus:border-[color:var(--pink)] px-3 py-2 text-lg font-mono uppercase outline-none tracking-widest mt-2"
              data-testid="party-code-input"
            />
            <button className="brutal-btn-pink brutal-btn w-full mt-4" onClick={doJoin} disabled={busy || !codeInput.trim()} data-testid="party-join-button">
              JOIN
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Lobby view
  const p = partyState;
  if (!p) return null;
  const isHost = (p.host || '').toUpperCase() === myTag;
  const draft = p.draft_state;
  const isDraftMode = !!draft;
  const draftDone = !!draft?.done;

  return (
    <div className="min-h-screen w-full px-4 md:px-6 py-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button className="brutal-btn text-sm" onClick={() => { stopPolling(); goToMenu(); }} data-testid="party-lobby-back">
          <ArrowLeft size={16} /> LEAVE
        </button>
        <div className="flex items-center gap-3">
          <div className="text-xs uppercase tracking-widest text-[color:var(--text-dim)]">ROOM</div>
          <div className="font-display text-4xl font-black text-[color:var(--pink)] tracking-widest" data-testid="party-room-code">{p.room_code}</div>
          <button className="brutal-btn text-xs px-2 py-1" onClick={copyCode} data-testid="party-copy-code">
            {copied ? <Check size={14}/> : <Copy size={14}/>}
          </button>
          {p.round_num > 1 && <span className="brutal-border-pink px-2 py-1 text-[10px] font-bold text-[color:var(--pink)]">ROUND {p.round_num}</span>}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="party-members-grid">
        {[0,1,2,3].map(i => {
          const m = p.members?.[i];
          if (!m) return (
            <div key={i} className="brutal-border-dim p-4 bg-[#0a0a0a] flex items-center justify-center min-h-[100px] text-[color:var(--text-dim)] uppercase text-sm">
              — EMPTY SLOT —
            </div>
          );
          const cls = m.class_id ? CLASSES[m.class_id] : null;
          return (
            <div key={i} className="brutal-border p-4 bg-[#0a0a0a]" data-testid={`party-member-${i}`}>
              <div className="flex items-center justify-between">
                <div className="font-display font-black text-xl uppercase text-[color:var(--neon)]">
                  {m.tag === p.host && <Crown size={14} className="inline mr-1 text-[color:var(--pink)]"/>}
                  {m.tag}
                </div>
                {cls && <span className="text-xs" style={{color: cls.color}}>{cls.icon} {cls.name}</span>}
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                <div className="brutal-border-dim px-2 py-1">
                  <span className="text-[color:var(--text-dim)]">FL </span>
                  <span className="font-bold text-white">{m.floor}</span>
                </div>
                <div className="brutal-border-dim px-2 py-1">
                  <span className="text-[color:var(--text-dim)]">SCORE </span>
                  <span className="font-bold text-[color:var(--neon)]">{m.score}</span>
                </div>
                <div className="brutal-border-dim px-2 py-1 text-center">
                  <span className={`font-bold uppercase text-[10px] ${m.status==='won'?'text-[color:var(--neon)]':m.status==='lost'?'text-[color:var(--red)]':m.status==='playing'?'text-[color:var(--pink)]':m.status==='drafting'?'text-[color:var(--pink)]':'text-[color:var(--text-dim)]'}`}>
                    {m.status}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Async Draft view */}
      {isDraftMode && !draftDone && (
        <div className="mt-6 brutal-border-pink p-4 bg-[#0a0a0a]" data-testid="party-draft-active">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-widest text-[color:var(--pink)] flex items-center gap-2">
              <Shuffle size={14}/> ASYNC DRAFT · TURN {draft.turn_idx + 1}
            </div>
            <div className="text-xs uppercase text-[color:var(--text-dim)]">
              PICKING: <span className="text-[color:var(--neon)] font-bold" data-testid="draft-active-picker">{draft.turn_order[draft.turn_idx % draft.turn_order.length]}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-2" data-testid="party-draft-pool">
            {draft.pool.map((cid, i) => {
              const picked = draft.picked[i];
              const isMyTurn = draft.turn_order[draft.turn_idx % draft.turn_order.length] === myTag;
              return (
                <div key={i} onClick={() => isMyTurn && !picked && pickCard(i)} style={{opacity: picked ? 0.25 : 1, cursor: picked || !isMyTurn ? 'not-allowed' : 'pointer'}}>
                  <GameCard cardId={cid} testid={`party-draft-card-${i}`} disabled={picked || !isMyTurn} small />
                </div>
              );
            })}
          </div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
            {draft.turn_order.map((tg, i) => {
              const picks = draft.picks_by_tag?.[tg] || [];
              return (
                <div key={i} className="brutal-border-dim p-2 bg-[#0a0a0a]" data-testid={`draft-tally-${tg}`}>
                  <div className="text-[10px] uppercase text-[color:var(--text-dim)]">{tg}</div>
                  <div className="font-display text-xl font-black text-[color:var(--neon)]">{picks.length}/{draft.picks_per_player}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {isDraftMode && draftDone && (
        <div className="mt-6 brutal-border p-4 bg-[#0a1a0a] flex items-center justify-between" data-testid="party-draft-complete">
          <div>
            <div className="text-xs uppercase tracking-widest text-[color:var(--neon)]">DRAFT COMPLETE</div>
            <div className="font-display text-2xl font-black text-white mt-1">Your deck: {(draft.picks_by_tag?.[myTag] || []).length} cards</div>
          </div>
          <button className="brutal-btn text-lg" onClick={finishDraftAndPlay} data-testid="party-draft-play">
            <Play size={18}/> DROP IN
          </button>
        </div>
      )}

      {/* Host controls */}
      {isHost && !p.seed && !isDraftMode && (
        <div className="mt-6 brutal-border-pink p-4 bg-[#0a0a0a]" data-testid="party-host-controls">
          <div className="text-xs uppercase tracking-widest text-[color:var(--pink)] mb-2">HOST · CHOOSE MODE</div>
          <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2">
            <input
              value={seedInput}
              onChange={e => setSeedInput(e.target.value.toUpperCase())}
              className="flex-1 bg-black border-2 border-[color:var(--muted)] focus:border-[color:var(--pink)] px-3 py-2 text-lg font-mono uppercase outline-none tracking-widest"
              data-testid="party-seed-input"
            />
            <button className="brutal-btn-pink brutal-btn" onClick={hostStart} data-testid="party-start-button">
              <Play size={16}/> START RUN
            </button>
            <button className="brutal-btn text-sm" onClick={startDraft} disabled={(p.members?.length || 0) < 2} data-testid="party-start-draft-button">
              <Shuffle size={16}/> ASYNC DRAFT
            </button>
          </div>
          {(p.members?.length || 0) < 2 && (
            <div className="text-[10px] text-[color:var(--text-dim)] mt-2 uppercase">Async draft needs 2+ members. Share the code!</div>
          )}
        </div>
      )}

      {/* Non-host waiting */}
      {!isHost && !p.seed && !isDraftMode && (
        <div className="mt-6 text-center text-[color:var(--text-dim)] uppercase text-sm tracking-widest" data-testid="party-waiting-host">
          waiting for host to launch...
        </div>
      )}

      {p.seed && !isHost && !isDraftMode && (
        <div className="mt-6 brutal-border p-4 bg-[#0a1a0a] flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-[color:var(--text-dim)]">SEED SET · JOIN THE RUN</div>
            <div className="font-display text-4xl font-black text-[color:var(--neon)]" data-testid="party-locked-seed">{p.seed}</div>
          </div>
          <button className="brutal-btn text-lg" onClick={joinerStart} data-testid="party-join-run-button">
            <Play size={18}/> DROP IN
          </button>
        </div>
      )}

      {/* Restart round (host only) */}
      {isHost && (p.seed || p.members?.some(m => m.status === 'won' || m.status === 'lost')) && (
        <div className="mt-4 flex justify-center">
          <button className="brutal-btn-pink brutal-btn text-sm" onClick={doReset} data-testid="party-reset-button">
            <RotateCw size={16}/> NEW ROUND
          </button>
        </div>
      )}
    </div>
  );
}
